import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Button, Layout, Spin } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import AppContext from '../../AppContext';
import { BASE_URL } from '../../Constants';
import OrganizerManagement from './OrganizerManagement';
import EventGatesPanel from './EventGatesPanel';

const { Content } = Layout;

// Standalone shortcut to the organizers and gates, so admins don't have to open the full event edit form.
const EventOrganizersPage = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { profile } = useContext(AppContext);
  const [event, setEvent] = useState(null);
  const [gates, setGates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const loadGates = useCallback(async () => {
    try {
      const response = await axios.get(`${BASE_URL}/Organizer/events/${id}/gates?includeInactive=true`);
      setGates(response.data || []);
    } catch (error) {
      console.error('Error loading gates:', error);
    }
  }, [id]);

  useEffect(() => {
    const loadEvent = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/Event/${id}?companyId=${profile.companyId}`);
        setEvent(response.data);
        await loadGates();
      } catch (error) {
        console.error('Error loading event:', error);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    loadEvent();
  }, [id, profile.companyId, loadGates]);

  const isArabicUi = (i18n.language || '').startsWith('ar');
  const title = (isArabicUi && event?.arabicTitle?.trim()) || event?.title;

  return (
    <Layout className="min-h-full bg-transparent">
      <header className="py-3 sm:py-4 bg-transparent">
        <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8 flex flex-wrap items-center gap-x-4 gap-y-2">
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/events')}>
            {t('organizerManagement.page.backToEvents')}
          </Button>
          <div style={{ minWidth: 0 }}>
            <h1 className="text-xl sm:text-3xl font-bold tracking-tight text-white break-words">
              {title || t('organizerManagement.page.title')}
            </h1>
          </div>
        </div>
      </header>
      <Content className="rounded-lg px-2 py-3 shadow bg-white sm:px-6 sm:py-6">
        {loading ? (
          <div className="text-center py-12"><Spin /></div>
        ) : notFound ? (
          <div className="text-center py-12 text-gray-500">{t('organizerManagement.page.notFound')}</div>
        ) : (
          <>
            <OrganizerManagement
              eventId={id}
              isNewEvent={false}
              hasGates={gates.some((g) => g.isActive !== false)}
              offersGuestContact={event?.eventType === 'marriage' || event?.showGuestContact === true}
            />
            <h2 className="text-base font-semibold mt-6 mb-3">{t('organizerManagement.page.gatesTitle')}</h2>
            <EventGatesPanel eventId={id} gates={gates} setGates={setGates} reload={loadGates} />
          </>
        )}
      </Content>
    </Layout>
  );
};

export default EventOrganizersPage;
